import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";

import useApiHelper from "../hooks/useApiHelper";
import { IContactMatch } from "../models/Contact";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";
import { contactMatchQuery } from "../utils/contactDuplicates";

// Existing contacts with the same email or the same mobile as the details being typed in, so staff
// can pick the existing person instead of creating a duplicate. Waits for a whole email or a
// number long enough to be a mobile before asking.
export default function useContactMatches(
  email: string | null | undefined,
  phone: string | null | undefined,
  excludeContactId?: string,
  componentName = "useContactMatches"
) {
  const { get } = useApiHelper(apiConfig(componentName));

  const debouncedEmail = useDebounce(email ?? "", 500);
  const debouncedPhone = useDebounce(phone ?? "", 500);

  const query = contactMatchQuery(debouncedEmail, debouncedPhone, excludeContactId);

  return useQuery({
    // Under "contacts" so anything that invalidates contacts refreshes the matches too.
    queryKey: ["contacts", "matches", query],
    queryFn: () => get<DefaultResponseWithData<IContactMatch[]>>(`/contact/matches?${query}`),
    enabled: query !== null,
    staleTime: 1000 * 30,
  });
}
